import React from 'react';
import PropTypes from 'prop-types';
import Collapse from '@mui/material/Collapse';
import Divider from '@mui/material/Divider';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import DownloadIcon from '@mui/icons-material/Download';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { formatMoneyWithSign } from '../../../helpers';
import formatDecimal from '../../../helpers/formatters';
import { DEFAULT_ICON_CLASS_NAME } from '../../../theme/constants/icon';
import useBooleanState from '../../../hooks/useBooleanState';
import CollapsibleSection from './CollapsibleSection';

const SummaryRow = ({ label, value, bold }) => (
  <Stack direction="row" justifyContent="space-between" py={0.75}>
    <Typography variant="small_2" fontWeight={bold ? 700 : 500} color="gray.A700">
      {label}
    </Typography>
    <Typography variant="small_2" fontWeight={bold ? 700 : 600} color="gray.A700">
      {value}
    </Typography>
  </Stack>
);

SummaryRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node.isRequired,
  bold: PropTypes.bool,
};

SummaryRow.defaultProps = {
  bold: false,
};

const OperationSummarySection = ({ operation, monthlyRate }) => {
  const [costsExpanded, toggleCosts] = useBooleanState(false);
  if (!operation) return null;
  const financedPercentage = operation.advancePercentage != null
    ? Math.round(Number(operation.advancePercentage))
    : null;
  const settlementUrl = operation.operationSettlement?.pdfFile;

  return (
    <CollapsibleSection
      title={`Resumen de tu operación N° ${operation.id}`}
      id="operation-summary-section"
    >
      <Stack mt={2}>
        <SummaryRow label="Cantidad de facturas" value={operation.invoicesCount ?? 0} />
        <SummaryRow label="Monto total" value={formatMoneyWithSign(operation.totalAmount)} />
        <SummaryRow
          label={financedPercentage ? `Monto financiado (${financedPercentage}%)` : 'Monto financiado'}
          value={formatMoneyWithSign(operation.financedAmount)}
        />
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          onClick={toggleCosts}
          sx={{ cursor: 'pointer', py: 0.75 }}
        >
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Typography variant="small_2" fontWeight={500} color="gray.A700">
              Costos totales
            </Typography>
            <ExpandMoreIcon
              className={DEFAULT_ICON_CLASS_NAME}
              sx={{
                fontSize: 18,
                transform: costsExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.3s ease-in-out',
              }}
            />
          </Stack>
          <Typography variant="small_2" fontWeight={600} color="gray.A700">
            {formatMoneyWithSign(operation.totalCosts)}
          </Typography>
        </Stack>
        <Collapse in={costsExpanded}>
          <Stack sx={{ pl: 1 }}>
            <SummaryRow label="Tasa operación" value={formatDecimal(monthlyRate)} />
            <SummaryRow
              label="Diferencia de precio"
              value={formatMoneyWithSign(operation.totalInterestAmount)}
            />
            <SummaryRow label="Comisión" value={formatMoneyWithSign(operation.commission)} />
          </Stack>
        </Collapse>
        <Divider sx={{ my: 1 }} />
        <SummaryRow label="Total a depositar" value={formatMoneyWithSign(operation.totalAdvanceAmount)} bold />
        {settlementUrl && (
          <Stack direction="row" justifyContent="center" mt={2}>
            <Link
              href={settlementUrl}
              target="_blank"
              rel="noopener noreferrer"
              underline="always"
              sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontWeight: 600 }}
              id="download-settlement-link"
            >
              <DownloadIcon sx={{ fontSize: 18 }} />
              Descargar liquidación
            </Link>
          </Stack>
        )}
      </Stack>
    </CollapsibleSection>
  );
};

const moneyShape = PropTypes.shape({
  amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
});

OperationSummarySection.propTypes = {
  operation: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    invoicesCount: PropTypes.number,
    totalAmount: moneyShape,
    totalAdvanceAmount: moneyShape,
    totalInterestAmount: moneyShape,
    financedAmount: moneyShape,
    totalCosts: moneyShape,
    advancePercentage: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    commission: moneyShape,
    operationSettlement: PropTypes.shape({ pdfFile: PropTypes.string }),
  }),
  monthlyRate: PropTypes.number,
};

OperationSummarySection.defaultProps = {
  operation: null,
  monthlyRate: undefined,
};

export default OperationSummarySection;
